import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FiltersContext from '@/context/FiltersContext/FiltersContext';
import { useAuthContext } from '@/context/AuthContext';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import {
  buildExternalFilterName,
  EXTERNAL_FILTER_PARAM,
  parseExternalFilterParams,
  resolveExternalCountry,
} from '@/utils/filter/externalFilters';
import { useToast } from '@/storybook/components/Toast/Toast';
import { useGetUserCountries } from '@/api/queries/country/useGetUserCountries';
import { useGetFilterOptions } from '@/api/queries/filter/useGetFilterOptions';
import { writeCachedCountryCodes } from '@/models/filter/configs/general/countryFilter';
import { FilterType } from '@/models/filter/FilterType';
import type { SelectItem } from '@/types/ui/selectItem';
import { isPeriod } from '@/utils/date/isPeriod';
import useSharedViewValidation from './useSharedViewValidation';

interface UseApplyExternalFiltersResult {
  isLoading: boolean;
}

const useApplyExternalFilters = (): UseApplyExternalFiltersResult => {
  const { t } = useTranslation();
  const { handleApplyFilter, filterState } = useContext(FiltersContext);
  const { userInfo } = useAuthContext();
  const { validateSharedFilters } = useSharedViewValidation();
  const { makeToast } = useToast();
  const { data: userCountries, isLoading: isLoadingUserCountries } = useGetUserCountries<SelectItem<string>[]>();
  const [searchParams, setSearchParams] = useSearchParams();
  const parsedFilters = parseExternalFilterParams(searchParams);
  const [isLoading, setIsLoading] = useState(parsedFilters !== null);
  const processedSearch = useRef<string | null>(null);
  const hasCountryFilter = Array.isArray(parsedFilters?.filters[FilterType.COUNTRY]);
  const canLoadCountryOptions = hasCountryFilter && isPeriod(filterState[FilterType.DATE_RANGE]);
  const { data: countryOptions = [], isPending: isCountryOptionsPending } = useGetFilterOptions<SelectItem<string>[]>({
    type: FilterType.COUNTRY,
    applyDynamicFilters: false,
    enabled: canLoadCountryOptions,
  });

  useEffect(() => {
    const currentSearch = searchParams.toString();
    if (!parsedFilters || processedSearch.current === currentSearch) return;

    if (hasCountryFilter && (isLoadingUserCountries || !canLoadCountryOptions || isCountryOptionsPending)) return;

    processedSearch.current = currentSearch;

    const applyExternalFilters = async (): Promise<void> => {
      try {
        if (Object.keys(parsedFilters.filters).length === 0) return;

        const countryCodes = parsedFilters.filters[FilterType.COUNTRY];
        const resolvedCountry =
          Array.isArray(countryCodes) && countryCodes.every(countryCode => typeof countryCode === 'string')
            ? resolveExternalCountry(countryCodes, userCountries ?? [], countryOptions)
            : { selectedPaths: [], countryCodes: [], unavailableCountryCodes: [] };
        const filters = {
          ...parsedFilters.filters,
          ...(hasCountryFilter && { [FilterType.COUNTRY]: resolvedCountry.selectedPaths }),
        };
        writeCachedCountryCodes(resolvedCountry.countryCodes);
        const validation = await validateSharedFilters(filters, [], undefined, undefined, true);
        handleApplyFilter(validation.validFilters);

        const missingValues = [
          ...validation.missingFilters.flatMap(missingFilter => missingFilter.missingValues),
          ...resolvedCountry.unavailableCountryCodes,
        ];
        if (missingValues.length > 0) {
          makeToast({
            content: String(
              t('global.filters.externalFilters.unavailableValues', { values: missingValues.join(', ') }),
            ),
            appearance: 'warning',
          });
        }

        try {
          const filterName = buildExternalFilterName(String(t('global.filters.externalFilters.name')), new Date());
          await UserFilterApiService.createFilter({
            filterType: 'saved_filter',
            filterName,
            isDefault: false,
            filters: {
              globalFilters: validation.validFilters,
              senderCountryCodes: userInfo?.countries ?? [],
            },
          });
        } catch {
          makeToast({ content: String(t('global.filters.externalFilters.saveError')), appearance: 'error' });
        }
      } catch {
        makeToast({ content: String(t('global.filters.externalFilters.applyError')), appearance: 'error' });
      } finally {
        setIsLoading(false);
        setSearchParams(
          previous => {
            const next = new URLSearchParams(previous);
            next.delete(EXTERNAL_FILTER_PARAM);
            parsedFilters.consumedKeys.forEach(key => next.delete(key));
            return next;
          },
          { replace: true },
        );
      }
    };

    applyExternalFilters();
  }, [
    handleApplyFilter,
    makeToast,
    parsedFilters,
    searchParams,
    setSearchParams,
    t,
    userCountries,
    hasCountryFilter,
    isLoadingUserCountries,
    countryOptions,
    isCountryOptionsPending,
    canLoadCountryOptions,
    userInfo,
    validateSharedFilters,
  ]);

  return { isLoading };
};

export default useApplyExternalFilters;
